import type { ProviderCapabilityStatus } from '../consts/provider-capabilities';

// ==================== ENUMS ====================

export enum AssociationType {
  global = 'global',
  regional = 'regional',
  national = 'national',
  industry_specific = 'industry_specific',
}

// ==================== SERVICE TYPE TYPES ====================

export interface CreateServiceTypeData {
  name: string;
  code: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateServiceTypeData {
  name?: string;
  code?: string;
  description?: string;
  isActive?: boolean;
}

export interface ServiceTypeResponseDto {
  id: string;
  name: string;
  code: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  services?: ServiceResponseDto[];
}

export interface ReorderServiceTypesData {
  orderedIds: string[];
}

// ==================== SERVICE TYPES ====================

export interface CreateServiceData {
  name: string;
  code: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateServiceData {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface ServiceResponseDto {
  id: string;
  serviceTypeId: string;
  serviceTypeName?: string;
  name: string;
  code: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ReorderServicesData {
  serviceTypeId: string;
  orderedIds: string[];
}

export interface ProviderServiceResponseDto {
  id: string;
  providerId: string;
  serviceId: string;
  isFeatured: boolean;
  status: ProviderCapabilityStatus;
  service: ServiceResponseDto;
}

export interface ServiceSummaryDto {
  id: string;
  name: string;
  isFeatured: boolean;
}

export interface ServiceWithTypeResponseDto {
  id: string;
  serviceTypeId: string;
  serviceTypeName: string;
  name: string;
  code: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ==================== ASSOCIATION TYPES ====================

export interface CreateAssociationData {
  name: string;
  code: string;
  description?: string;
  logoUrl?: string;
  websiteUrl?: string;
  associationType: AssociationType;
  isActive?: boolean;
}

export interface UpdateAssociationData {
  code?: string;
  name?: string;
  description?: string;
  logoUrl?: string;
  websiteUrl?: string;
  associationType?: AssociationType;
  isActive?: boolean;
}

export interface AssociationResponseDto {
  id: string;
  name: string;
  code: string;
  description: string | null;
  logoUrl: string | null;
  websiteUrl: string | null;
  associationType: AssociationType;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ReorderAssociationsData {
  orderedIds: string[];
}

export interface AddProviderAssociationData {
  associationId: string;
}

export interface ProviderAssociationResponseDto {
  id: string;
  providerId: string;
  associationId: string;
  status: ProviderCapabilityStatus;
  association: AssociationResponseDto;
}

export interface AssociationSummaryDto {
  id: string;
  name: string;
  code: string;
  description: string | null;
  websiteUrl: string | null;
}

// ==================== CONTAINER TYPES ====================

export interface CreateContainerTypeData {
  name: string;
  code: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateContainerTypeData {
  name?: string;
  code?: string;
  description?: string;
  isActive?: boolean;
}

export interface ContainerTypeResponseDto {
  id: string;
  name: string;
  code: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  sizes?: ContainerSizeResponseDto[];
}

export interface ReorderContainerTypesData {
  orderedIds: string[];
}

export interface CreateContainerSizeData {
  name: string;
  code: string;
  isActive?: boolean;
}

export interface UpdateContainerSizeData {
  name?: string;
  code?: string;
  isActive?: boolean;
}

export interface ContainerSizeResponseDto {
  id: string;
  containerTypeId: string;
  containerTypeName?: string;
  name: string;
  code: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ReorderContainerSizesData {
  containerTypeId: string;
  orderedIds: string[];
}

// ==================== PROVIDER SERVICE TYPES ====================

export interface AddProviderServiceData {
  serviceId: string;
  isFeatured?: boolean;
}

export interface ProviderServiceInput {
  serviceId: string;
  isFeatured?: boolean;
  status?: ProviderCapabilityStatus;
}

export interface SetProviderServicesData {
  services: ProviderServiceInput[];
}

// ==================== SHIPMENT TYPE TYPES ====================

export interface CreateShipmentTypeData {
  name: string;
  code: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateShipmentTypeData {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface ShipmentTypeResponseDto {
  id: string;
  name: string;
  code: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ReorderShipmentTypesData {
  orderedIds: string[];
}

export interface ProviderShipmentTypeResponseDto {
  id: string;
  providerId: string;
  shipmentTypeId: string;
  isFeatured: boolean;
  status: ProviderCapabilityStatus;
  shipmentType: ShipmentTypeResponseDto;
}

export interface ShipmentTypeSummaryDto {
  id: string;
  name: string;
  isFeatured: boolean;
}

export interface ProviderShipmentTypeInput {
  shipmentTypeId: string;
  isFeatured?: boolean;
  status?: ProviderCapabilityStatus;
}

export interface SetProviderShipmentTypesData {
  shipmentTypes: ProviderShipmentTypeInput[];
}

// ==================== COMMODITY TYPES ====================

export interface CreateCommodityData {
  name: string;
  code: string;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface UpdateCommodityData {
  name?: string;
  code?: string;
  description?: string;
  isActive?: boolean;
}

export interface CommodityResponseDto {
  id: string;
  name: string;
  code: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ReorderCommoditiesData {
  orderedIds: string[];
}

export interface ProviderCommodityResponseDto {
  id: string;
  providerId: string;
  commodityId: string;
  isFeatured: boolean;
  status: ProviderCapabilityStatus;
  commodity: CommodityResponseDto;
}

export interface CommoditySummaryDto {
  id: string;
  name: string;
  isFeatured: boolean;
}

export interface ProviderCommodityInput {
  commodityId: string;
  isFeatured?: boolean;
  status?: ProviderCapabilityStatus;
}

export interface SetProviderCommoditiesData {
  commodities: ProviderCommodityInput[];
}

// ==================== CERTIFICATION TYPES ====================

export interface CreateCertificationData {
  name: string;
  code: string;
  description?: string;
  logoUrl?: string;
  websiteUrl?: string;
  isActive?: boolean;
}

export interface UpdateCertificationData {
  name?: string;
  description?: string;
  logoUrl?: string;
  websiteUrl?: string;
  isActive?: boolean;
}

export interface CertificationResponseDto {
  id: string;
  name: string;
  code: string;
  description: string | null;
  logoUrl: string | null;
  websiteUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ReorderCertificationsData {
  orderedIds: string[];
}

export interface ProviderCertificationResponseDto {
  id: string;
  providerId: string;
  certificationId: string;
  status: ProviderCapabilityStatus;
  certification: CertificationResponseDto;
}

export interface CertificationSummaryDto {
  id: string;
  name: string;
  code: string;
  description: string | null;
  websiteUrl: string | null;
}

export interface ProviderCertificationInput {
  certificationId: string;
  status?: ProviderCapabilityStatus;
}

export interface ProviderTypeCapabilityResponseDto {
  id?: string;
  providerId: string;
  providerTypeOptionId: string;
  code: import('./provider.types').ProviderType;
  name: string;
  status: ProviderCapabilityStatus;
}

export interface ProviderTransportModeCapabilityResponseDto {
  id?: string;
  providerId: string;
  transportModeOptionId: string;
  code: import('./provider.types').TransportMode;
  name: string;
  status: ProviderCapabilityStatus;
}

export interface ProviderTypeCapabilityInput {
  providerTypeOptionId: string;
  status: ProviderCapabilityStatus;
}

export interface ProviderTransportModeCapabilityInput {
  transportModeOptionId: string;
  status: ProviderCapabilityStatus;
}

export interface SetProviderCertificationsData {
  certifications: ProviderCertificationInput[];
}
